import { useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { postWalletQuote, walletError, type WalletErrorKind, type WalletQuote, type WalletQuoteType } from '@/api/parent';
import { PinPrompt, QuoteBox, Sheet, ghost, primary } from './ui';

interface Props {
  title: string;
  quoteType: WalletQuoteType;
  amount: number;
  amountLabel?: string;
  details?: { label: string; value: string }[];
  balance?: number;
  /** Runs the money request; the same idempotency key is reused on retry. Return the success text to show. */
  run: (pin: string, key: string) => Promise<string | void>;
  onCancel: () => void;
  onFinished?: () => void;
  onNeedWallet?: () => void;
  onNeedPin?: () => void;
}

/** Review (amount + every charge = total) → PIN → done. Used for fee payments and transfers. */
export function WalletPinAction({ title, quoteType, amount, amountLabel, details, balance, run, onCancel, onFinished, onNeedWallet, onNeedPin }: Props) {
  const [quote, setQuote] = useState<WalletQuote | null>(null);
  const [error, setError] = useState<{ kind: WalletErrorKind; message: string } | null>(null);
  const [askPin, setAskPin] = useState(false);
  const [pinErr, setPinErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const key = useRef(crypto.randomUUID());

  useEffect(() => {
    let live = true;
    postWalletQuote({ type: quoteType, amount })
      .then((q) => live && setQuote(q))
      .catch((e) => {
        if (!live) return;
        const w = walletError(e);
        setError(w);
        if (w.kind === 'no_wallet') onNeedWallet?.();
        if (w.kind === 'pin_not_set') onNeedPin?.();
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteType, amount]);

  const short = quote && balance != null && balance < quote.total;

  const submit = async (pin: string) => {
    setBusy(true); setError(null); setPinErr('');
    try {
      const msg = await run(pin, key.current);
      setAskPin(false);
      setDone(msg || 'Done.');
    } catch (e) {
      const w = walletError(e);
      if (w.kind === 'wrong_pin') { setPinErr(w.message); return; }
      setAskPin(false);
      setError(w);
      if (w.kind === 'no_wallet') onNeedWallet?.();
      if (w.kind === 'pin_not_set') onNeedPin?.();
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Sheet title="Successful" onClose={() => { onFinished?.(); onCancel(); }} footer={<button onClick={() => { onFinished?.(); onCancel(); }} className={primary}>Done</button>}>
        <div className="text-center py-4"><CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" /><p className="mt-3 text-slate-800">{done}</p></div>
      </Sheet>
    );
  }

  return (
    <>
      <Sheet title={title} sub="Check the details, then confirm with your PIN." onClose={onCancel}
        footer={<><button onClick={onCancel} disabled={busy} className={ghost}>Cancel</button>
          <button onClick={() => { setError(null); setAskPin(true); }} disabled={!quote || busy || !!short || (!!error && !['provider_failed', 'unavailable', 'other'].includes(error.kind))} className={primary}>{error ? 'Try again' : 'Continue'}</button></>}>
        <div className="space-y-3">
          {!!details?.length && (
            <dl className="text-sm space-y-1.5">
              {details.map((d) => <div key={d.label} className="flex justify-between gap-4"><dt className="text-slate-500">{d.label}</dt><dd className="text-right font-medium text-slate-900">{d.value}</dd></div>)}
            </dl>
          )}
          <QuoteBox quote={quote} amount={amount} amountLabel={amountLabel} loading={!quote && !error} />
          {balance != null && <p className={`text-xs ${short ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>Wallet balance: {balance.toLocaleString('en-NG', { style: 'currency', currency: 'NGN' })}{short ? ' — not enough. Top up first.' : ''}</p>}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error.message}</p>}
        </div>
      </Sheet>
      {askPin && <PinPrompt title={title} error={pinErr} busy={busy} onCancel={() => setAskPin(false)} onConfirm={submit} />}
    </>
  );
}
